import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Check, ChefHat, Utensils, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function PartnerOnboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [rest, setRest] = useState(null);
  const [profile, setProfile] = useState({});
  const [hasPackages, setHasPackages] = useState(true);
  const [hasMenu, setHasMenu] = useState(false);
  const [servesAlcohol, setServesAlcohol] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/partner/me");
      if (!data.restaurant) {
        toast.error("No restaurant linked to your account");
        return;
      }
      setRest(data.restaurant);
      setProfile({
        name: data.restaurant.name || "",
        blurb: data.restaurant.blurb || "",
        contact_phone: data.restaurant.contact_phone || "",
        contact_email: data.restaurant.contact_email || "",
        gstin: data.restaurant.gstin || "",
      });
      const m = data.restaurant.dining_model;
      setHasPackages(m !== "a_la_carte");
      setHasMenu(m !== "package_only");
      setServesAlcohol(!!data.restaurant.serves_alcohol);
      // If already onboarded, allow editing — but don't auto-skip.
    })();
  }, []);

  const dining_model = hasPackages && hasMenu ? "hybrid" : hasPackages ? "package_only" : hasMenu ? "a_la_carte" : null;

  const finish = async () => {
    if (!dining_model) return toast.error("Pick at least one operating model");
    setSaving(true);
    try {
      await api.post("/partner/onboarding/complete", {
        ...profile,
        dining_model,
        serves_alcohol: servesAlcohol,
      });
      toast.success("Onboarding complete. Welcome to Tablo!");
      nav("/partner/inbox");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!rest) return <div className="py-10 text-light-ink animate-pulse">Loading...</div>;

  return (
    <div className="max-w-2xl mx-auto py-8 animate-fade-up">
      <header className="mb-8">
        <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">Welcome to Tablo</p>
        <h1 className="font-serif text-4xl">Let's set up <span className="italic">{rest.name}</span>.</h1>
        <p className="text-light-ink text-sm mt-2">3 quick steps so groups can start booking. You can change everything later.</p>

        <div className="flex items-center gap-2 mt-6">
          {[1, 2, 3].map((n) => (
            <div key={n} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${step >= n ? "bg-ink text-cream" : "bg-cream text-light-ink border border-tablo-border"}`} data-testid={`onb-step-dot-${n}`}>
                {step > n ? <Check className="w-3 h-3"/> : n}
              </div>
              {n < 3 && <div className={`w-12 h-px ${step > n ? "bg-ink" : "bg-tablo-border"}`}/>}
            </div>
          ))}
        </div>
      </header>

      {step === 1 && (
        <div className="space-y-4 tablo-card p-6" data-testid="onb-step-1">
          <h2 className="font-serif text-2xl mb-2">Profile</h2>
          <div>
            <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Restaurant name</label>
            <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="bg-white border-tablo-border" data-testid="onb-name"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">One-line description</label>
            <Textarea value={profile.blurb} onChange={(e) => setProfile({ ...profile, blurb: e.target.value })} className="bg-white border-tablo-border min-h-[60px]" data-testid="onb-blurb"/>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Contact phone</label>
              <Input value={profile.contact_phone} onChange={(e) => setProfile({ ...profile, contact_phone: e.target.value })} className="bg-white border-tablo-border" data-testid="onb-phone"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Contact email</label>
              <Input value={profile.contact_email} onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })} className="bg-white border-tablo-border" data-testid="onb-email"/>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">GSTIN</label>
            <Input value={profile.gstin} onChange={(e) => setProfile({ ...profile, gstin: e.target.value })} placeholder="36AAACO1234A1Z5" className="bg-white border-tablo-border" data-testid="onb-gstin"/>
          </div>
          <Button onClick={() => setStep(2)} className="w-full tablo-btn-primary" data-testid="onb-next-1">Continue <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2}/></Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4 tablo-card p-6" data-testid="onb-step-2">
          <h2 className="font-serif text-2xl mb-1">How do groups book at <span className="italic">{rest.name}</span>?</h2>
          <p className="text-sm text-light-ink mb-4">Pick one or both. You can switch later.</p>

          <button
            type="button"
            onClick={() => setHasPackages(!hasPackages)}
            className={`w-full text-left tablo-card p-5 transition-all ${hasPackages ? "ring-2 ring-amber bg-amber-soft/10" : "hover:shadow-tablo-lg"}`}
            data-testid="onb-toggle-packages"
          >
            <div className="flex items-start gap-3">
              <ChefHat className="w-5 h-5 text-amber mt-0.5" strokeWidth={1.6}/>
              <div className="flex-1">
                <p className="font-serif text-lg">Fixed packages</p>
                <p className="text-xs text-light-ink mt-1">You set price per head + inclusions; your chef decides the menu and portions based on pax. Best for banquet halls, clubs, big-format restaurants.</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${hasPackages ? "bg-ink border-ink" : "border-tablo-border"}`}>
                {hasPackages && <Check className="w-3 h-3 text-cream" strokeWidth={3}/>}
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setHasMenu(!hasMenu)}
            className={`w-full text-left tablo-card p-5 transition-all ${hasMenu ? "ring-2 ring-amber bg-amber-soft/10" : "hover:shadow-tablo-lg"}`}
            data-testid="onb-toggle-menu"
          >
            <div className="flex items-start gap-3">
              <Utensils className="w-5 h-5 text-amber mt-0.5" strokeWidth={1.6}/>
              <div className="flex-1">
                <p className="font-serif text-lg">Full menu (à-la-carte)</p>
                <p className="text-xs text-light-ink mt-1">Groups pick from your menu within their budget — either via Tablo Curate AI or themselves. Best for cafés, fine-dining, family restaurants.</p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${hasMenu ? "bg-ink border-ink" : "border-tablo-border"}`}>
                {hasMenu && <Check className="w-3 h-3 text-cream" strokeWidth={3}/>}
              </div>
            </div>
          </button>

          <div className="tablo-card p-5 flex items-center justify-between mt-2">
            <div>
              <p className="font-medium text-sm">We serve alcohol</p>
              <p className="text-xs text-light-ink">Beer, wine, cocktails will appear on your menu.</p>
            </div>
            <Switch checked={servesAlcohol} onCheckedChange={setServesAlcohol} data-testid="onb-alcohol"/>
          </div>

          <div className="flex gap-3">
            <Button onClick={() => setStep(1)} className="tablo-btn-outline flex-1" data-testid="onb-back-2">Back</Button>
            <Button onClick={() => setStep(3)} disabled={!hasPackages && !hasMenu} className="tablo-btn-primary flex-1" data-testid="onb-next-2">Continue <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2}/></Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4 tablo-card p-6" data-testid="onb-step-3">
          <h2 className="font-serif text-2xl mb-1">You're set.</h2>
          <p className="text-sm text-light-ink">Here's what we'll show bookers:</p>

          <div className="bg-cream rounded-xl p-4 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-light-ink">Restaurant</span><span className="font-medium">{profile.name}</span></div>
            <div className="flex justify-between"><span className="text-light-ink">Operating model</span><span className="font-medium">{dining_model.replace("_", " ")}</span></div>
            <div className="flex justify-between"><span className="text-light-ink">Serves alcohol</span><span className="font-medium">{servesAlcohol ? "Yes" : "No"}</span></div>
            <div className="flex justify-between"><span className="text-light-ink">GSTIN</span><span className="font-medium font-mono text-xs">{profile.gstin || "—"}</span></div>
          </div>

          <div className="space-y-2 text-sm text-light-ink">
            <p>Next: from your dashboard you can</p>
            <ul className="list-disc list-inside text-xs space-y-1">
              {hasPackages && <li>Add packages (price per head, inclusions, slots)</li>}
              {hasMenu && <li>Add your menu items (manually now; PDF/image upload soon)</li>}
              <li>Receive booking requests in your Inbox</li>
            </ul>
          </div>

          <div className="flex gap-3">
            <Button onClick={() => setStep(2)} className="tablo-btn-outline flex-1" data-testid="onb-back-3">Back</Button>
            <Button onClick={finish} disabled={saving} className="tablo-btn-primary flex-1" data-testid="onb-finish">{saving ? "Saving..." : "Go to dashboard"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}
